import {
  CatalogElectrificationLevel,
  CatalogElectrificationLevelLabels,
  CatalogFuel,
  CatalogFuelLabels,
  CatalogMotorSourceAxle,
  type VehicleCatalogModificationRecord
} from '@navio/nsdk';
import { cva, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  NavioInfoCard,
  NavioPowerLabel,
  NoddiButton,
  NoddiCard,
  NoddiLink,
  NoddiTooltip,
  type NavioInfoCardItem
} from 'noddi-ui-web';
import {
  catalogNameWithoutMarkets,
  catalogNodeLabel,
  catalogPowerText,
  formatCatalogYears,
  formatMeasure,
  getLabelMapFromEntries,
  shownChoice
} from 'noddi-util';
import type { ReactNode } from 'react';
import { CatalogMarketBadges } from './CatalogMarketBadges';

const FUEL_LABEL = getLabelMapFromEntries(CatalogFuel, CatalogFuelLabels);
const ELECTRIFICATION_LABEL = getLabelMapFromEntries(CatalogElectrificationLevel, CatalogElectrificationLevelLabels);
const ELECTRIFICATION_ICON: Partial<Record<CatalogElectrificationLevel, NoddiIconName>> = {
  [CatalogElectrificationLevel.BEV]: 'Power',
  [CatalogElectrificationLevel.EREV]: 'RefreshCw',
  [CatalogElectrificationLevel.FCEV]: 'Atom',
  [CatalogElectrificationLevel.FULL_HYBRID]: 'Combine',
  [CatalogElectrificationLevel.MILD_HYBRID]: 'Activity',
  [CatalogElectrificationLevel.PHEV]: 'PlugZap'
};

type Props = {
  modification: VehicleCatalogModificationRecord;
  href: string;
  generationHref?: string;
  onEdit?: () => void;
};

export const VehicleModificationCard = ({ modification, href, generationHref, onEdit }: Props) => {
  const nameWithoutMarkets = catalogNameWithoutMarkets(modification.name);
  const powertrain = modification.powertrain;
  const power = powertrain?.headlinePowerKw ?? powertrain?.systemPowerKw ?? powertrain?.enginePowerKw;
  const name = power != null ? nameWithoutMarkets.replace(/\s+\d+(?:[.,]\d+)?\s*kW\s*$/i, '') : nameWithoutMarkets;
  const fuel = powertrain != null && shownChoice(powertrain.primaryFuel) ? FUEL_LABEL[powertrain.primaryFuel] : null;
  const powertrainIcons: { label: string; icon: NoddiIconName }[] = [];
  if (fuel != null && powertrain != null) {
    powertrainIcons.push({
      label: `Fuel: ${fuel}`,
      icon: powertrain.primaryFuel === CatalogFuel.ELECTRIC ? 'Zap' : 'Fuel'
    });
  }
  if (powertrain != null && shownChoice(powertrain.secondaryFuel)) {
    powertrainIcons.push({
      label: `Secondary fuel: ${FUEL_LABEL[powertrain.secondaryFuel]}`,
      icon: powertrain.secondaryFuel === CatalogFuel.ELECTRIC ? 'Zap' : 'Fuel'
    });
  }
  if (powertrain != null && shownChoice(powertrain.electrificationLevel)) {
    powertrainIcons.push({
      label: `Electrification: ${ELECTRIFICATION_LABEL[powertrain.electrificationLevel]}`,
      icon: ELECTRIFICATION_ICON[powertrain.electrificationLevel] ?? 'PlugZap'
    });
  }
  const trim = modification.trim;
  const attributes = trim?.trimAttributeLabels ?? [];
  const years = formatCatalogYears(modification.yearFrom, modification.yearTo);
  const summary = [
    powertrain?.engineDisplacementLitres != null ? `${formatMeasure(powertrain.engineDisplacementLitres)} L` : null,
    ...attributes,
    ...(trim?.bodyTypeLabels ?? [])
  ]
    .filter(Boolean)
    .join(' · ');
  const items: (NavioInfoCardItem | false | null | undefined)[] = [
    powertrain?.engineDisplacementLitres != null &&
      modificationFact('Engine size', `${formatMeasure(powertrain.engineDisplacementLitres)} L`),
    modificationFact('Engine code', powertrain?.engineCode),
    modificationFact('Engine layout', powertrain?.engineLayoutLabel),
    powertrain?.enginePowerKw != null &&
      powertrain.enginePowerKw !== power &&
      modificationPowerFact('Engine power', powertrain.enginePowerKw),
    powertrain?.systemPowerKw != null &&
      powertrain.systemPowerKw !== power &&
      modificationPowerFact('System power', powertrain.systemPowerKw),
    powertrain?.secondaryFuelEnginePowerKw != null &&
      modificationPowerFact('Secondary fuel power', powertrain.secondaryFuelEnginePowerKw),
    ...(powertrain?.motors ?? []).map((motor) =>
      modificationFact(
        motor.sourceAxle === CatalogMotorSourceAxle.FRONT
          ? 'Front motor'
          : motor.sourceAxle === CatalogMotorSourceAxle.REAR
            ? 'Rear motor'
            : 'Motor',
        [motor.code, motor.peakPowerKw != null ? catalogPowerText(motor.peakPowerKw) : null]
          .filter(Boolean)
          .join(' · '),
        <span className='flex flex-wrap items-center gap-1.5'>
          {motor.code}
          {motor.peakPowerKw != null && <NavioPowerLabel kilowatts={motor.peakPowerKw} />}
        </span>
      )
    ),
    modificationFact('Drivetrain and trim', attributes.join(' · ')),
    modificationFact('Trim level', trim?.trimLevelLabels?.join(' · ')),
    modificationFact('Body style', trim?.bodyTypeLabels?.join(' · ')),
    modificationFact('Body code', trim?.bodyCode),
    modification.generation != null &&
      !generationHref && {
        label: 'Model generation',
        value: catalogNodeLabel(modification.generation),
        readOnly: true
      },
    modificationFact('Aliases', modification.aliases.join(' · '))
  ];
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <article className='group relative h-full min-w-0'>
          <NoddiCard
            size='small'
            title={name}
            className='h-full transition-shadow group-hover:shadow-md [&_[data-slot=card-header]]:flex-nowrap [&_[data-slot=card-header]]:items-start'
            description={years || undefined}
            headerNode={
              <CatalogMarketBadges
                markets={modification.markets}
                className='relative z-20 max-w-1/2 shrink-0 justify-end'
              />
            }
            contentClassName='flex min-w-0 flex-1 flex-col gap-4'
          >
            {(power != null || powertrainIcons.length > 0) && (
              <div className='flex flex-wrap items-center justify-between gap-4'>
                {power != null && <NavioPowerLabel kilowatts={power} />}
                {powertrainIcons.length > 0 && (
                  <div className='relative z-20 flex items-center gap-4'>
                    {powertrainIcons.map(({ label, icon }) => (
                      <NoddiTooltip
                        key={label}
                        label={label}
                        content={label}
                        triggerClassName='min-h-6 min-w-6 justify-center text-muted-foreground'
                        trigger={<NoddiIcon name={icon} size='small' />}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {summary && <p className='text-sm text-muted-foreground'>{summary}</p>}
            <Collapsible className='flex flex-1 flex-col gap-4'>
              {items.some(Boolean) && (
                <CollapsibleContent className='relative z-20'>
                  <NavioInfoCard items={items} className='border-0 p-0 shadow-none' />
                </CollapsibleContent>
              )}
              <div className='mt-auto flex items-center justify-between gap-4'>
                <NoddiTooltip
                  content={modification.hasFitments ? 'Fitments available' : 'No fitments'}
                  label={modification.hasFitments ? 'Fitments available' : 'No fitments'}
                  trigger={
                    <span
                      className={fitmentStatusVariants({ available: modification.hasFitments })}
                      role='img'
                      aria-label={modification.hasFitments ? 'Fitments available' : 'No fitments'}
                    >
                      <NoddiIcon name={modification.hasFitments ? 'CircleCheck' : 'CircleX'} size='small' />
                    </span>
                  }
                />
                <div className='relative z-20 flex items-center gap-4'>
                  {items.some(Boolean) && (
                    <CollapsibleTrigger asChild>
                      <NoddiButton
                        variant='outline'
                        size='small'
                        className='size-8 p-0'
                        aria-label={`Specifications for ${name}`}
                        title='Specifications'
                      >
                        <NoddiIcon name='List' size='small' />
                      </NoddiButton>
                    </CollapsibleTrigger>
                  )}
                  {modification.generation != null && generationHref && (
                    <NoddiLink
                      to={generationHref}
                      variant='outline'
                      size='small'
                      className='relative z-20 size-8 p-0'
                      aria-label={`View generation ${catalogNodeLabel(modification.generation)}`}
                      title={`Generation: ${catalogNodeLabel(modification.generation)}`}
                    >
                      <NoddiIcon name='GalleryVerticalEnd' size='small' />
                    </NoddiLink>
                  )}
                </div>
              </div>
            </Collapsible>
          </NoddiCard>
          <NoddiLink
            to={href}
            variant='link'
            className='absolute inset-0 z-10 size-full rounded-xl p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden'
            aria-label={`View ${name} fitment`}
          />
        </article>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem asChild>
          <NoddiLink to={href} variant='link' startIcon='ArrowRight'>
            View fitment
          </NoddiLink>
        </ContextMenuItem>
        {onEdit && (
          <ContextMenuItem onSelect={onEdit}>
            <NoddiIcon name='Edit' size='small' />
            Edit modification
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

const modificationFact = (
  label: string,
  value: string | null | undefined,
  content?: ReactNode
): NavioInfoCardItem | false =>
  value != null &&
  value !== '' && {
    label,
    value: content ?? value,
    copyText: value,
    readOnly: true
  };

const modificationPowerFact = (label: string, kilowatts: number): NavioInfoCardItem => ({
  label,
  value: <NavioPowerLabel kilowatts={kilowatts} />,
  copyText: catalogPowerText(kilowatts),
  readOnly: true
});

const fitmentStatusVariants = cva('relative z-20 inline-flex items-center', {
  variants: {
    available: {
      true: 'text-successDark',
      false: 'text-destructive'
    }
  }
});
